/**
 * Reset Password Page
 */
import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, CheckCircle, AlertCircle, Eye, EyeOff } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Logo Component
const Logo = ({ className = "h-10 w-10" }) => (
  <img 
    src="https://customer-assets.emergentagent.com/job_f315b95a-0efe-40b5-9cbe-765ee1480209/artifacts/fawkrmzt_logo%20512%20.512.png" 
    alt="Infirmly" 
    className={className}
  />
);

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (token) {
      verifyToken();
    } else {
      setVerifying(false);
    }
  }, [token]);

  const verifyToken = async () => {
    try {
      const res = await axios.get(`${API}/auth/verify-reset-token/${token}`);
      setTokenValid(true);
      setEmail(res.data.email);
    } catch (error) {
      setTokenValid(false);
      toast.error('Ungültiger oder abgelaufener Token');
    } finally {
      setVerifying(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error('Passwörter stimmen nicht überein');
      return;
    }
    
    if (password.length < 6) {
      toast.error('Passwort muss mindestens 6 Zeichen haben');
      return;
    }
    
    setLoading(true);
    
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: password
      });
      setSuccess(true);
      toast.success('Passwort erfolgreich zurückgesetzt!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ein Fehler ist aufgetreten');
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#0044CC]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-3 mb-6">
            <Logo className="h-12 w-12" />
            <span className="font-bold text-2xl" style={{ fontFamily: 'Chivo, sans-serif' }}>Infirmly Swiss</span>
          </Link>
          <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Neues Passwort setzen
          </h1>
        </div>

        <Card className="border-gray-200 shadow-sm">
          <CardContent className="p-6">
            {!token ? (
              <div className="text-center space-y-4">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
                  <AlertCircle className="w-8 h-8 text-red-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Kein Token gefunden</h3>
                  <p className="text-gray-500 text-sm mt-2">
                    Der Reset-Link ist ungültig. Bitte fordern Sie einen neuen Link an.
                  </p>
                </div>
                <Button onClick={() => navigate('/forgot-password')} className="bg-[#0044CC]">
                  Neuen Link anfordern
                </Button>
              </div>
            ) : !tokenValid ? (
              <div className="text-center space-y-4">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
                  <AlertCircle className="w-8 h-8 text-red-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Ungültiger Token</h3>
                  <p className="text-gray-500 text-sm mt-2">
                    Der Reset-Link ist abgelaufen oder ungültig. Bitte fordern Sie einen neuen Link an.
                  </p>
                </div>
                <Button onClick={() => navigate('/forgot-password')} className="bg-[#0044CC]">
                  Neuen Link anfordern
                </Button>
              </div>
            ) : success ? (
              <div className="text-center space-y-4">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Passwort zurückgesetzt!</h3>
                  <p className="text-gray-500 text-sm mt-2">
                    Ihr Passwort wurde erfolgreich geändert. Sie können sich jetzt anmelden.
                  </p>
                </div>
                <Button onClick={() => navigate('/login')} className="bg-[#0044CC]">
                  Zur Anmeldung
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="bg-blue-50 p-3 rounded-lg mb-4">
                  <p className="text-sm text-blue-800">
                    Neues Passwort für: <strong>{email}</strong>
                  </p>
                </div>
                
                <div>
                  <Label htmlFor="password">Neues Passwort</Label>
                  <div className="relative mt-1">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="h-11 pr-10"
                      required
                      minLength={6}
                      data-testid="new-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="confirmPassword">Passwort bestätigen</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="mt-1 h-11"
                    required
                    data-testid="confirm-password-input"
                  />
                </div>
                
                <Button 
                  type="submit" 
                  className="w-full h-11 bg-[#0044CC] hover:bg-[#003399] text-white font-medium rounded-full"
                  disabled={loading}
                  data-testid="reset-submit-btn"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Passwort zurücksetzen'}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>

        <p className="text-center mt-6 text-gray-500">
          <Link to="/login" className="text-[#0044CC] hover:underline font-medium">
            Zurück zur Anmeldung
          </Link>
        </p>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
